import React, { useState, useEffect } from 'react';
import { teamAPI, departmentAPI } from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';

const TeamManager = () => {
    const [members, setMembers] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [formData, setFormData] = useState({ name: '', role: '', bio: '', order: 0, isActive: true, departmentId: '' });
    const [departments, setDepartments] = useState([]);
    const [showDeptForm, setShowDeptForm] = useState(false);
    const [editingDeptId, setEditingDeptId] = useState(null);
    const [deptName, setDeptName] = useState('');

    const loadDepartments = async () => {
        try {
            const res = await departmentAPI.getAll();
            setDepartments(res.data || []);
        } catch (err) {
            toast.error('Failed to load departments');
        }
    };

    const resetDeptForm = () => {
        setEditingDeptId(null);
        setDeptName('');
        setShowDeptForm(false);
    };

    const handleDeptSubmit = async (e) => {
        e.preventDefault();
        if (!deptName.trim()) { toast.error('Department name is required'); return; }
        try {
            if (editingDeptId) {
                await departmentAPI.update(editingDeptId, { name: deptName.trim() });
                toast.success('Department updated');
            } else {
                await departmentAPI.create({ name: deptName.trim() });
                toast.success('Department created');
            }
            resetDeptForm();
            loadDepartments();
            loadMembers();
        } catch (err) {
            toast.error('Operation failed: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeptEdit = (d) => {
        setEditingDeptId(d.id);
        setDeptName(d.name);
        setShowDeptForm(true);
    };

    const handleDeptDelete = async (id) => {
        if (!window.confirm('Delete this department? Members in it will be left without a department.')) return;
        try {
            await departmentAPI.delete(id);
            toast.success('Department deleted');
            loadDepartments();
            loadMembers();
        } catch (err) {
            toast.error('Failed to delete department');
        }
    };

    const loadMembers = async () => {
        try {
            const res = await teamAPI.getAll();
            setMembers(res.data || []);
        } catch (err) {
            toast.error('Failed to load team members');
        }
    };

    useEffect(() => { loadMembers(); loadDepartments(); }, []);

    const resetForm = () => {
        setEditingId(null);
        setFormData({ name: '', role: '', bio: '', order: 0, isActive: true, departmentId: '' });
        setImageFile(null);
        setShowForm(false);
    };

    const handleEdit = (m) => {
        setEditingId(m.id);
        setFormData({ name: m.name || '', role: m.role || '', bio: m.bio || '', order: m.order || 0, isActive: m.isActive, departmentId: m.departmentId || '' });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this team member?')) return;
        try {
            await teamAPI.delete(id);
            toast.success('Team member deleted');
            loadMembers();
        } catch (err) {
            toast.error('Failed to delete team member');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        // client-side validation
        const validate = () => {
            if (!formData.name || formData.name.trim().length < 2) return 'Name is required (min 2 chars)';
            if (isNaN(Number(formData.order)) || Number(formData.order) < 0) return 'Order must be a non-negative number';
            if (imageFile) {
                if (!imageFile.type.startsWith('image/')) return 'Uploaded file must be an image';
                if (imageFile.size > 5 * 1024 * 1024) return 'Image must be smaller than 5MB';
            }
            return null;
        };
        const validationError = validate();
        if (validationError) {
            toast.error(validationError);
            setLoading(false);
            return;
        }
        try {
            const payload = new FormData();
            payload.append('name', formData.name);
            payload.append('role', formData.role);
            payload.append('bio', formData.bio);
            payload.append('departmentId', formData.departmentId || '');
            payload.append('order', formData.order);
            payload.append('isActive', formData.isActive);
            if (imageFile) payload.append('image', imageFile);

            if (editingId) {
                await teamAPI.update(editingId, payload);
                toast.success('Team member updated');
            } else {
                await teamAPI.create(payload);
                toast.success('Team member created');
            }
            resetForm();
            loadMembers();
        } catch (err) {
            toast.error('Operation failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6">
            <Toaster />
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">{showDeptForm ? 'Department Management' : 'Team Management'}</h1>
                <div className="flex gap-2">
                <button onClick={() => { if (showDeptForm) resetDeptForm(); else { setShowForm(false); setShowDeptForm(true); } }} className={`px-4 py-2 rounded-md font-semibold text-white ${showDeptForm ? 'bg-gray-600' : 'bg-orange-600'}`}>
                    {showDeptForm ? '← Back to Team Members' : '+ Add Department'}
                </button>
                {!showDeptForm && <button onClick={() => setShowForm(!showForm)} className={`px-4 py-2 rounded-md font-semibold text-white ${showForm ? 'bg-red-500' : 'bg-orange-600'}`}>
                    {showForm ? 'Cancel' : '+ Add Team Member'}
                </button>}
                </div>
            </div>

            {showDeptForm ? (
            <>
                <form onSubmit={handleDeptSubmit} className="bg-white p-6 rounded-lg shadow-md mb-6 flex gap-2">
                    <input required placeholder="Department name" value={deptName} onChange={(e) => setDeptName(e.target.value)} className="p-2 border rounded flex-1" />
                    <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded">{editingDeptId ? 'Update Department' : 'Create Department'}</button>
                    <button type="button" onClick={resetDeptForm} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                </form>

            <h2 className="text-xl font-bold mb-3">Departments</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {departments.length === 0 && <p className="text-sm text-gray-500">No departments yet.</p>}
                {departments.map(d => (
                    <div key={d.id} className="bg-white rounded-lg shadow-md p-4 flex justify-between items-center">
                        <span className="font-semibold">{d.name}</span>
                        <div className="flex gap-2">
                            <button onClick={() => handleDeptEdit(d)} className="px-3 py-1 bg-blue-600 text-white rounded">Edit</button>
                            <button onClick={() => handleDeptDelete(d.id)} className="px-3 py-1 bg-red-600 text-white rounded">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
            </>
            ) : (
            <>
            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input required placeholder="Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="p-2 border rounded" />
                    <input placeholder="Role" value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="p-2 border rounded" />
                    <select value={formData.departmentId} onChange={(e) => setFormData({...formData, departmentId: e.target.value})} className="p-2 border rounded">
                        <option value="">Select Department</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <textarea placeholder="Bio" value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} className="p-2 border rounded md:col-span-2" />
                    <input type="number" placeholder="Order" value={formData.order} onChange={(e) => setFormData({...formData, order: Number(e.target.value)})} className="p-2 border rounded" />
                    <div className="flex items-center gap-2">
                        <label className="text-sm">Active:</label>
                        <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({...formData, isActive: e.target.checked})} />
                    </div>
                    <input type="file" onChange={(e) => setImageFile(e.target.files[0])} className="p-2" />
                    <div className="md:col-span-2 flex gap-2">
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded">{loading ? 'Saving...' : (editingId ? 'Update Member' : 'Create Member')}</button>
                        <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                    </div>
                </form>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {members.map(m => (
                    <div key={m.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                        {m.imageUrl && <img src={`${process.env.REACT_APP_API_URL}${m.imageUrl}`} alt={m.name} className="w-full h-48 object-cover" />}
                        <div className="p-4">
                            <h3 className="text-lg font-bold">{m.name}</h3>
                            <p className="text-sm text-gray-600 mb-2">{m.role}</p>
                            {m.departmentName && <p className="text-sm text-gray-500 mb-2">{m.departmentName}</p>}
                            <p className="text-sm text-gray-700 mb-2">{m.bio}</p>
                            <div className="flex gap-2">
                                <button onClick={() => handleEdit(m)} className="px-3 py-1 bg-blue-600 text-white rounded">Edit</button>
                                <button onClick={() => handleDelete(m.id)} className="px-3 py-1 bg-red-600 text-white rounded">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            </>
            )}
        </div>
    );
};
export default TeamManager;